/**
 * Category Service - Fetch service categories from public API
 */

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon_name: string;
  color_from: string;
  color_to: string;
  image_url: string;
  homepage_ranking: number;
  job_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Subcategory {
  id: string;
  category_id: string;
  name: string;
  slug: string;
  description: string;
  job_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoriesResponse {
  data: Category[];
  count: number;
}

export interface SubcategoriesResponse {
  data: Subcategory[];
  count: number;
  category_id: string;
}

const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'https://npm.rapidfix.ie';

/**
 * Fetch all active service categories from the public API
 */
export async function fetchCategories(): Promise<Category[]> {
  try {
    const response = await fetch(`${PUBLIC_API_BASE_URL}/api/v1/public/categories`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`);
    }

    const result: CategoriesResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
}

/**
 * Fetch subcategories for a specific category
 */
export async function fetchSubcategories(categoryId: string): Promise<Subcategory[]> {
  try {
    const response = await fetch(`${PUBLIC_API_BASE_URL}/api/v1/public/categories/${categoryId}/subcategories`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch subcategories: ${response.statusText}`);
    }

    const result: SubcategoriesResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching subcategories:', error);
    throw error;
  }
}

/**
 * Fetch homepage categories (top 8)
 */
export async function fetchHomepageCategories(): Promise<Category[]> {
  try {
    const response = await fetch(`${PUBLIC_API_BASE_URL}/api/v1/public/categories/homepage`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch homepage categories: ${response.statusText}`);
    }

    const result: CategoriesResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching homepage categories:', error);
    throw error;
  }
}
